import { findOption } from 'components/Widget/shared/types';
import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import { STATE_TAX_SCHEDULES } from './stateTaxBrackets';
import type {
  ExemptionQuestion,
  FilingStatusOption,
  PaycheckExemptions,
  PaycheckSegmentKey,
  PayFrequency,
  PayFrequencyOption,
  PayType,
  PayTypeOption,
  StateKey,
  TaxStateOption,
  TaxYear,
  YesNoOption,
} from './types';

export const TITLE = 'Paycheck Calculator';

export const WEEKS_PER_YEAR = 52;

export const PAY_FREQUENCY_OPTIONS: readonly PayFrequencyOption[] = [
  { key: 'weekly', label: 'Weekly', periodsPerYear: WEEKS_PER_YEAR },
  { key: 'biweekly', label: 'Bi-weekly', periodsPerYear: 26 },
  { key: 'semimonthly', label: 'Semi-monthly', periodsPerYear: 24 },
  { key: 'monthly', label: 'Monthly', periodsPerYear: 12 },
];

export const getPayFrequency = (key: PayFrequency): PayFrequencyOption =>
  findOption(PAY_FREQUENCY_OPTIONS, key, 'pay frequency');

/**
 * Brackets and standard deduction from IRS Rev. Proc. 2025-32 (sections 4.01 and 4.14); the wage
 * base from the SSA contribution and benefit base. All three are re-indexed every year.
 */
const TAX_YEAR_2026: TaxYear = {
  year: 2026,
  standardDeduction: { single: 16100, marriedJointly: 32200, marriedSeparately: 16100 },
  brackets: {
    single: [
      { floor: 0, rate: 0.1 },
      { floor: 12400, rate: 0.12 },
      { floor: 50400, rate: 0.22 },
      { floor: 105700, rate: 0.24 },
      { floor: 201775, rate: 0.32 },
      { floor: 256225, rate: 0.35 },
      { floor: 640600, rate: 0.37 },
    ],
    marriedJointly: [
      { floor: 0, rate: 0.1 },
      { floor: 24800, rate: 0.12 },
      { floor: 100800, rate: 0.22 },
      { floor: 211400, rate: 0.24 },
      { floor: 403550, rate: 0.32 },
      { floor: 512450, rate: 0.35 },
      { floor: 768700, rate: 0.37 },
    ],
    marriedSeparately: [
      { floor: 0, rate: 0.1 },
      { floor: 12400, rate: 0.12 },
      { floor: 50400, rate: 0.22 },
      { floor: 105700, rate: 0.24 },
      { floor: 201775, rate: 0.32 },
      { floor: 256225, rate: 0.35 },
      { floor: 384350, rate: 0.37 },
    ],
  },
  socialSecurityWageBase: 184500,
};

export const CURRENT_TAX_YEAR = TAX_YEAR_2026;

export const SOCIAL_SECURITY_RATE = 0.062;

export const MEDICARE_RATE = 0.0145;

export const ADDITIONAL_MEDICARE_RATE = 0.009;

/**
 * Statutory and not indexed, so it lives outside the tax year. It is the employer-withholding
 * threshold, which is why it does not vary by filing status the way the liability does.
 */
export const ADDITIONAL_MEDICARE_THRESHOLD = 200000;

/** Marks every string Legal has yet to supply, so it cannot pass for approved copy in a QA screenshot. */
const PLACEHOLDER = '[PLACEHOLDER COPY - PENDING LEGAL]';

export const DISCLAIMER =
  'This calculator is for educational purposes only and is not intended to offer any tax, legal, financial, or investment advice.';

export const TAX_FOUNDATION_URL = 'https://taxfoundation.org/data/all/state/state-income-tax-rates-2026/';

/** Each omission is one listed in scripts/stateTaxBrackets/README.md, so the two change together. */
export const STATE_TAX_DISCLOSURE = {
  heading: `${PLACEHOLDER} How state income tax is estimated`,
  method:
    "We subtract your state's standard deduction from your annual gross pay, apply your state's income tax brackets for your filing status, and divide the result by your number of pay periods. Married filing separately uses the single brackets and deduction. If your state doesn't tax wages, your state income tax is $0.",
  sourceLead: 'Brackets and deductions come from the Tax Foundation,',
  sourceTitle: 'State Individual Income Tax Rates and Brackets, 2026',
  sourceRest:
    ". They reflect state law as of February 11, 2026. Where a state's 2026 figures weren't published yet, the prior year's figures are used.",
  omissionsLead: "The estimate doesn't include:",
  omissions: [
    'Personal and dependent exemptions',
    'Tax credits, phase-outs, recaptures, and minimum taxes',
    'Fixed dollar amounts some states charge on top of their rates',
    'Separate tax tables some states use for certain incomes',
    'Deductions for federal income tax paid',
    'Local income taxes charged by a city or county',
    'State payroll taxes withheld from wages, such as disability or paid family leave insurance',
  ],
} as const;

export const FORM_LABEL = 'Paycheck calculator';

export const PAY_FREQUENCY_FIELD: CalculatorField = { label: 'How often are you paid?' };

export const TAX_STATE_FIELD: CalculatorField = {
  label: 'Where do you file taxes?',
  hint: 'Which state do you file your federal and state taxes with?',
};

const isStateKey = (key: string): key is StateKey => key in STATE_TAX_SCHEDULES;

/** In the generated table's order, which is alphabetical by name rather than by postal code. */
export const TAX_STATE_OPTIONS: readonly TaxStateOption[] = Object.entries(STATE_TAX_SCHEDULES).flatMap(
  ([key, { name }]) => (isStateKey(key) ? [{ key, label: name }] : []),
);

export const FILING_STATUS_FIELD: CalculatorField = {
  label: 'Filing status',
  hint: 'Do you file your taxes as single or married (filing jointly or separately)? This personal classification sets your tax rates.',
};

export const FILING_STATUS_OPTIONS: readonly FilingStatusOption[] = [
  { key: 'single', label: 'Single' },
  { key: 'marriedJointly', label: 'Married filing jointly' },
  { key: 'marriedSeparately', label: 'Married filing separately' },
];

export const PAY_TYPE_LEGEND = 'How are you paid?';

export const PAY_TYPE_OPTIONS: readonly PayTypeOption[] = [
  { key: 'hourly', label: 'Hourly' },
  { key: 'salary', label: 'Salary' },
];

export const DEFAULT_PAY_TYPE: PayType = 'hourly';

export const getPayType = (key: PayType): PayTypeOption => findOption(PAY_TYPE_OPTIONS, key, 'pay type');

export const DEFAULT_HOURLY_WAGE = '25';

/** Design's greyed defaults; `valueOrPlaceholder` makes Calculate fall back to these same numbers. */
export const HOURLY_WAGE_FIELD: CalculatorField = {
  label: 'Hourly wage',
  hint: 'An hourly wage is a set amount of money an employee is paid for each hour of work they perform.',
  placeholder: DEFAULT_HOURLY_WAGE,
};

export const HOURLY_WAGE_SUFFIX = '/ hr';

export const WEEKLY_HOURS_FIELD: CalculatorField = {
  label: 'Average weekly hours',
  hint: 'The number of hours an employee works per week, not including overtime, typically calculated over a rolling weekly period.',
  placeholder: '40',
};

export const ANNUAL_SALARY_FIELD: CalculatorField = {
  label: 'Gross annual salary',
  hint: 'The total amount of money an employee earns in one year before any taxes or deductions are taken out.',
  placeholder: '60,000',
};

export const ANNUAL_SALARY_SUFFIX = '/ year';

export const OVERTIME_SWITCH_LABEL = 'Overtime pay';

export const OVERTIME_MULTIPLIER = 1.5;

/** No placeholder here: it is 1.5x whatever the hourly wage currently resolves to, built at render and Calculate. */
export const OVERTIME_WAGE_FIELD: CalculatorField = { label: 'Overtime hourly wage', hint: PLACEHOLDER };

export const OVERTIME_HOURS_FIELD: CalculatorField = {
  label: 'Average overtime weekly hours',
  hint: PLACEHOLDER,
  placeholder: '5',
};

/** Every hour in a week, the only ceiling a weekly figure has. */
export const MAX_WEEKLY_HOURS = 168;

/** Two, so a timesheet's quarter hours (37.25) survive; one decimal could not hold them. */
export const WEEKLY_HOURS_FRACTION_DIGITS = 2;

/** Holds the worst case, 168 hours paid monthly, to a gross the result column can fit (about $7.3M). */
export const MAX_HOURLY_WAGE = 9999.99;

/** Room for 1.5x the top hourly wage, so the greyed overtime default never fails validation on its own. */
export const MAX_OVERTIME_WAGE = 14999.99;

/** Still clears the top federal bracket and both FICA high-earner rules. */
export const MAX_ANNUAL_SALARY = 999999;

export const PAY_FREQUENCY_ERROR = "Select how often you're paid";

export const FILING_STATUS_ERROR = 'Select your filing status';

export const TAX_STATE_ERROR = 'Select your state';

export const HOURLY_WAGE_MIN_ERROR = 'Enter an hourly wage greater than $0';

export const HOURLY_WAGE_MAX_ERROR = `Enter an hourly wage up to $${MAX_HOURLY_WAGE.toLocaleString('en-US')}`;

export const ANNUAL_SALARY_MIN_ERROR = 'Enter a salary greater than $0';

export const ANNUAL_SALARY_MAX_ERROR = `Enter a salary up to $${MAX_ANNUAL_SALARY.toLocaleString('en-US')}`;

export const WEEKLY_HOURS_ERROR = `Enter hours greater than 0, up to ${MAX_WEEKLY_HOURS}`;

export const OVERTIME_WAGE_MIN_ERROR = 'Enter an overtime wage of $0 or more';

export const OVERTIME_WAGE_MAX_ERROR = `Enter an overtime wage up to $${MAX_OVERTIME_WAGE.toLocaleString('en-US')}`;

export const OVERTIME_HOURS_ERROR = 'Enter overtime hours of 0 or more';

export const TOTAL_WEEKLY_HOURS_ERROR = `Regular and overtime hours can total up to ${MAX_WEEKLY_HOURS} a week`;

export const YES_NO_OPTIONS: readonly YesNoOption[] = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
];

export const EXEMPTION_QUESTIONS: readonly ExemptionQuestion[] = [
  { key: 'federal', legend: 'Are you exempt from Federal Income Tax Withholding?', hint: PLACEHOLDER },
  { key: 'socialSecurity', legend: 'Are you exempt from Social Security Tax?', hint: PLACEHOLDER },
  { key: 'medicare', legend: 'Are you exempt from Medicare Tax?', hint: PLACEHOLDER },
];

export const NO_EXEMPTIONS: PaycheckExemptions = { federal: false, socialSecurity: false, medicare: false };

export const INITIAL_CURRENCY_VALUES: CalculatorFieldValues = {
  [HOURLY_WAGE_FIELD.label]: '',
  [ANNUAL_SALARY_FIELD.label]: '',
  [OVERTIME_WAGE_FIELD.label]: '',
};

export const INITIAL_HOURS_VALUES: CalculatorFieldValues = {
  [WEEKLY_HOURS_FIELD.label]: '',
  [OVERTIME_HOURS_FIELD.label]: '',
};

/** Lowercased because it only ever appears mid-sentence, as in "Est. bi-weekly take-home pay". */
export const frequencyPhrase = (key: PayFrequency): string => getPayFrequency(key).label.toLowerCase();

/** Gross leads because every other line is carved out of it. */
export const RESULT_ROWS = [
  { key: 'gross', label: (frequency: string) => `Gross ${frequency} income` },
  { key: 'takeHome', label: () => 'Take-home pay' },
  { key: 'federal', label: () => 'Federal income tax' },
  { key: 'state', label: () => 'State income tax' },
  { key: 'socialSecurity', label: () => 'Social Security' },
  { key: 'medicare', label: () => 'Medicare' },
] as const;

/** Clockwise from 12 o'clock in the legend's row order, so the ring and the table read in step. */
export const SEGMENT_ORDER: readonly PaycheckSegmentKey[] = [
  'takeHome',
  'federal',
  'state',
  'socialSecurity',
  'medicare',
];

/**
 * Text colours: the donut strokes with `currentColor` and the legend swatch fills with `bg-current`,
 * so one class colours both and the two cannot drift.
 */
export const SEGMENT_COLORS: Record<PaycheckSegmentKey, string> = {
  takeHome: 'text-accent-green-400',
  federal: 'text-accent-green-600',
  // Custom lime: the nearest token, Verde, and chimekit's unused greens are near-white against the card.
  state: 'text-[#C1EA73]',
  socialSecurity: 'text-accent-green-500',
  medicare: 'text-accent-green-300',
};

export const RESULT_COPY = {
  eyebrow: (frequency: string) => `Est. ${frequency} take-home pay`,
} as const;

/** Self-contained copy for the live-region announcement; the panel itself is too long to hear out. */
export const SUMMARY_COPY = {
  takeHome: (frequency: string, takeHome: string, gross: string) =>
    `Your estimated ${frequency} take-home pay is ${takeHome}, out of ${gross} gross income.`,
} as const;
